import { requireCompletedProfile } from "@hisani/auth";
import { canManageStableMembers, canOverrideMemberPermission, } from "../domain/membership/authorization-policy.js";
import { normalizeContactPointValue } from "../domain/membership/contact-point-normalization.js";
import { structuralCeiling } from "../domain/membership/entity-roles.js";
import { changeEntityRoleSchema, inviteStaffSchema, removeStaffSchema, setPermissionOverrideSchema, stableConflict, stableForbidden, stableInvalidTransition, stableNotFound, } from "../validation.js";
async function requireStaffManager(dependencies, stableId, horsemanId) {
    const membership = await dependencies.repository.getMembershipContext(stableId, horsemanId);
    if (membership === null)
        throw stableNotFound();
    if (!canManageStableMembers(membership))
        throw stableForbidden();
    return membership;
}
export async function inviteStaffMember(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = inviteStaffSchema.parse(input);
    await requireStaffManager(dependencies, parsed.stableId, actor.account.id);
    const now = dependencies.clock.now();
    const policy = await dependencies.policy.get();
    const normalizedContactPoint = normalizeContactPointValue(parsed.contactPointType, parsed.contactPointValue);
    const result = await dependencies.repository.inviteStaffMember({
        id: dependencies.newId(),
        stableId: parsed.stableId,
        actorId: actor.account.id,
        contactPointType: parsed.contactPointType,
        normalizedContactPoint,
        entityRole: parsed.entityRole,
        now,
        inviteExpiresAt: new Date(now.getTime() + policy.staffInviteExpiryDays * 24 * 60 * 60 * 1000),
    });
    if (result.outcome === "ALREADY_MEMBER") {
        throw stableConflict("This Horseman is already staff at this Stable.");
    }
    if (result.outcome === "ALREADY_INVITED") {
        throw stableConflict("An invite is already pending for this Contact Point at this Stable.");
    }
    return result.member;
}
export async function removeStaffMember(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = removeStaffSchema.parse(input);
    await requireStaffManager(dependencies, parsed.stableId, actor.account.id);
    const now = dependencies.clock.now();
    const outcome = await dependencies.repository.removeStaffMember({
        stableId: parsed.stableId,
        membershipId: parsed.membershipId,
        actorId: actor.account.id,
        now,
    });
    if (outcome === "NOT_FOUND")
        throw stableNotFound();
    if (outcome === "LAST_TRAINER") {
        throw stableInvalidTransition("A Stable must keep at least one Trainer or Co-Trainer.");
    }
    return { removed: true };
}
export async function changeEntityRole(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = changeEntityRoleSchema.parse(input);
    await requireStaffManager(dependencies, parsed.stableId, actor.account.id);
    const now = dependencies.clock.now();
    const outcome = await dependencies.repository.changeEntityRole({
        stableId: parsed.stableId,
        membershipId: parsed.membershipId,
        actorId: actor.account.id,
        newRole: parsed.newRole,
        now,
    });
    if (outcome === "NOT_FOUND")
        throw stableNotFound();
    if (outcome === "LAST_TRAINER") {
        throw stableInvalidTransition("A Stable must keep at least one Trainer or Co-Trainer.");
    }
    return { updated: true };
}
export async function setPermissionOverride(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = setPermissionOverrideSchema.parse(input);
    const membership = await dependencies.repository.getMembershipContext(parsed.stableId, actor.account.id);
    if (membership === null)
        throw stableNotFound();
    // The target's current role is resolved authoritatively server-side —
    // never trusted from the request body — so a client cannot claim a lower
    // role to smuggle in an override above that role's real structural
    // ceiling (root AGENTS.md §3: "never trust client-provided role/
    // permission claims").
    const targetRole = await dependencies.repository.getMembershipRole(parsed.stableId, parsed.membershipId);
    if (targetRole === null)
        throw stableNotFound();
    if (!canOverrideMemberPermission(membership, targetRole)) {
        throw stableForbidden();
    }
    if (parsed.accessLevel !== null &&
        structuralCeiling(targetRole, parsed.feature) === "NONE" &&
        parsed.accessLevel !== "NONE") {
        throw stableForbidden();
    }
    const now = dependencies.clock.now();
    const outcome = await dependencies.repository.setPermissionOverride({
        stableId: parsed.stableId,
        membershipId: parsed.membershipId,
        actorId: actor.account.id,
        feature: parsed.feature,
        accessLevel: parsed.accessLevel,
        now,
    });
    if (outcome === "NOT_FOUND")
        throw stableNotFound();
    return { updated: true };
}
export async function listStaff(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const membership = await dependencies.repository.getMembershipContext(input.stableId, actor.account.id);
    if (membership === null)
        throw stableNotFound();
    return dependencies.repository.listStaff(input.stableId);
}
